import { computed, unref } from 'vue'

// Campaign amounts from Laravel model accessors come through as DOLLARS
// (the PHP accessors divide stored-cents by 100 before serializing).
const formatCurrency = (dollars) => {
  const n = Number(dollars) || 0
  return `$${Math.round(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
}

const formatCurrencyCompact = (dollars) => {
  const n = Number(dollars) || 0
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`
  return `$${Math.round(n)}`
}

export function useCampaignProgress(campaignSource, { milestoneCount = 4 } = {}) {
  // Accept a plain object, a ref, or a getter function (Vue's typical composable input styles).
  const campaign = computed(() => {
    const src = typeof campaignSource === 'function' ? campaignSource() : unref(campaignSource)
    return src || {}
  })

  const raisedDollars = computed(() => Number(campaign.value.total_raised_with_children_campaigns) || 0)
  const goalDollars = computed(() => Number(campaign.value.goal) || 0)

  const percent = computed(() => {
    if (!goalDollars.value) return 0
    return Math.min(Math.max(Math.round((raisedDollars.value / goalDollars.value) * 100), 0), 100)
  })

  const gapDollars = computed(() => Math.max(goalDollars.value - raisedDollars.value, 0))

  const milestones = computed(() => {
    if (!goalDollars.value || milestoneCount < 1) return []
    const step = goalDollars.value / milestoneCount
    return Array.from({ length: milestoneCount }, (_, i) => {
      const value = Math.round(step * (i + 1))
      return {
        value,
        label: formatCurrencyCompact(value),
        reached: raisedDollars.value >= value,
      }
    })
  })

  return {
    raisedDollars,
    goalDollars,
    percent,
    gapDollars,
    milestones,
    formattedRaised: computed(() => formatCurrency(raisedDollars.value)),
    formattedGoal: computed(() => formatCurrency(goalDollars.value)),
    formattedGap: computed(() => formatCurrencyCompact(gapDollars.value)),
  }
}

export { formatCurrency, formatCurrencyCompact }
